import React from 'react';

import { useImagesReady } from '@/shared/hooks/useImagesReady';
import '@/shared/styles/ArtworkReveal.css';

import '../styles/waitlist.css';

const BACKGROUND_SRC = '/images/waitlist/background.png';
const BRANDMARK_SRC = '/images/login/brandmark.svg';
const WORDMARK_SRC = '/images/login/logo-text.svg';

/** Six robot illustrations around the card (Figma 9507:1929), positioned in waitlist.css. */
const ROBOTS = [1, 2, 3, 4, 5, 6].map((index) => ({ index, src: `/images/waitlist/robot-${index}.svg` }));

/** Every artwork layer is revealed together once all of these have decoded. */
export const WAITLIST_ARTWORK_SOURCES = [BACKGROUND_SRC, ...ROBOTS.map((robot) => robot.src)];

/**
 * Brand lockup shared by every waiting-list screen (brandmark + wordmark).
 * `size="small"` is the 24.961 x 29.527 / 142.72 x 32 lockup used on the
 * wizard and confirmation cards; the default is the landing's 31.201 x 36.909
 * / 178.4 x 40 lockup.
 */
export function WaitlistLogo({ size = 'large' }) {
    const className = size === 'small' ? 'waitlist-logo waitlist-logo-small' : 'waitlist-logo';
    return (
        <div className={className} role="img" aria-label="specific ai">
            <img className="waitlist-logo-brandmark" src={BRANDMARK_SRC} alt="" />
            <img className="waitlist-logo-text" src={WORDMARK_SRC} alt="" />
        </div>
    );
}

/**
 * Page chrome for the waiting list (Figma MVP-DESIGNES 9507:1929 and
 * siblings): full-bleed gradient backdrop with a 5% overlay, six robot
 * illustrations along the edges, two rotated glass panels, a top-right action
 * slot and the 760px content card.
 *
 * `variant` picks the card geometry: `landing` (centred, gap 45),
 * `wizard` (left-aligned, gap 24) or `done` (left-aligned, lower on the page).
 *
 * The backdrop and the robots are faded in together once every file has
 * decoded (`useImagesReady`), so the multi-megabyte gradient and the SVGs do
 * not pop in one after another on first load.
 */
export default function WaitlistLayout({ variant = 'landing', topRight = null, children }) {
    const artworkReady = useImagesReady(WAITLIST_ARTWORK_SOURCES);
    const reveal = `artwork-reveal${artworkReady ? ' is-ready' : ''}`;
    return (
        <div className={`waitlist-page waitlist-page-${variant}`}>
            <div className={`waitlist-backdrop ${reveal}`} aria-hidden="true">
                <img className="waitlist-backdrop-image" src={BACKGROUND_SRC} alt="" fetchpriority="high" decoding="async" />
                <div className="waitlist-backdrop-overlay" />
            </div>
            <div className={`waitlist-robots ${reveal}`} aria-hidden="true">
                {ROBOTS.map(({ index, src }) => (
                    <img key={index} className={`waitlist-robot waitlist-robot-${index}`} src={src} alt="" decoding="async" />
                ))}
            </div>
            <div className="waitlist-glass waitlist-glass-outer" aria-hidden="true">
                <div className="waitlist-glass-panel" />
            </div>
            <div className="waitlist-glass waitlist-glass-inner" aria-hidden="true">
                <div className="waitlist-glass-panel" />
            </div>
            {topRight ? <div className="waitlist-top-right">{topRight}</div> : null}
            <main className={`waitlist-card waitlist-card-${variant} fs-unmask`}>{children}</main>
        </div>
    );
}
